import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Copy,
  Loader2,
  MapPin,
  Package,
  Phone,
  Scissors,
  Shield,
  User
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StatusBadge from "../../components/common/StatusBadge";
import { convertTo12Hour } from "../../utils/formatDateTime";
import { formatDate } from "../../utils/helpers";
import { showError, showSuccess } from "../../utils/toastUtils";


const UserBookingDetails = () => {
  const navigate = useNavigate();
  const params = useParams();

  const isPackage = params.type === "package";
  const bookingId = params.bookingId;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [otpStatus, setOtpStatus] = useState(null);

  // ── endpoints ───────────────────────────────────────────────────────────────
  const fetchEndpoint = isPackage
    ? `${process.env.REACT_APP_API_URL}/package-bookings/${bookingId}`
    : `${process.env.REACT_APP_API_URL}/service-bookings/get-by-id/${bookingId}`;

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails();
      fetchOTPStatus();
    }
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(fetchEndpoint);
      setBooking(response.data.data);
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      showError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const fetchOTPStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/staff-assignment/otp-status/${bookingId}`,
      );
      setOtpStatus(response.data.data);
    } catch {
      // OTP might not exist yet — safe to ignore
    }
  };

  const copyOTP = () => {
    if (booking?.otp?.code) {
      navigator.clipboard.writeText(booking.otp.code);
      showSuccess("OTP copied to clipboard");
    }
  };

  // ── derived values ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-500">Booking not found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          Go Back
        </button>
      </div>
    );
  }

  const scheduleDate = new Date(
    booking.rescheduledDate || booking.slotAvailabilityId?.date,
  );

  const totalAmount = booking?.totalAmount ?? 0;
  const manualDiscount = booking?.manualDiscount ?? 0;
  const couponDiscount = booking?.coupon?.discountApplied ?? 0;

  const totalDiscount = manualDiscount + couponDiscount;

  const finalPayable = booking?.finalPayableAmount ?? totalAmount;

  const discountPct =
    totalAmount > 0 && totalDiscount > 0
      ? ((totalDiscount / totalAmount) * 100).toFixed(1)
      : "0";

  const hasDiscount = totalDiscount > 0;
  const remainingHours = booking?.otp?.expiresAt
    ? Math.max(
      0,
      Math.ceil(
        (new Date(booking.otp.expiresAt) - new Date()) / (1000 * 60 * 60),
      ),
    )
    : null;

  // package-specific data
  const pkgName =
    booking?.packageSnapshot?.name || booking?.packageId?.name || "Package";
  const pkgCategory =
    booking?.packageSnapshot?.category ||
    booking?.packageId?.category ||
    "";
  const pkgServices =
    booking?.packageSnapshot?.services ||
    booking?.packageId?.services ||
    [];
  const pkgOriginalPrice =
    booking?.packageSnapshot?.price || booking?.packageId?.price || 0;
  const pkgDiscountedPrice =
    booking?.packageSnapshot?.discountedPrice ||
    booking?.packageId?.discountedPrice ||
    null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 truncate">
              {isPackage && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-semibold whitespace-nowrap">
                  <Package size={10} /> Package
                </span>
              )}
              Booking Details
            </h1>
            <p className="text-xs text-gray-500">
              ID: {bookingId?.slice(0, 8)?.toUpperCase()}
            </p>
          </div>
          <StatusBadge status={booking.status} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* ── OTP Card — shown for both types when staff assigned + OTP exists ── */}
        {booking.staffId && booking.otp && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-brandAccentColor/10 to-purple-50 rounded-2xl border-2 border-brandAccentColor p-4 md:p-5"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-brandAccentColor rounded-full flex items-center justify-center">
                  <Shield size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-bold text-gray-900">
                    Service Verification OTP
                  </h3>
                  <p className="text-xs text-gray-600">
                    {otpStatus?.isVerified
                      ? "✓ Verified"
                      : "Share with staff when they arrive"}
                  </p>
                </div>
              </div>
            </div>

            {otpStatus?.isVerified ? (
              <div className="bg-green-100 border border-green-300 rounded-xl p-3 text-center">
                <p className="text-green-800 font-semibold text-sm">
                  ✓ OTP Verified Successfully
                </p>
                <p className="text-green-600 text-xs mt-1">
                  Verified at{" "}
                  {new Date(otpStatus.verifiedAt).toLocaleString()}
                </p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 mb-1">
                        Your OTP Code
                      </p>
                      <div className="flex items-center gap-3">
                        <p className="text-3xl md:text-4xl font-bold text-primaryColor tracking-wider font-mono">
                          {booking.otp.code}
                        </p>
                        <button
                          onClick={copyOTP}
                          className="p-2 hover:bg-gray-100 rounded-lg transition active:scale-95"
                        >
                          <Copy size={18} className="text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        {otpStatus?.isExpired ? (
                          <span className="text-red-600 font-medium">
                            OTP Expired
                          </span>
                        ) : (
                          <>
                            Expires in{" "}
                            <span className="font-semibold">
                              {remainingHours} hour
                              {remainingHours !== 1 ? "s" : ""}
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* {otpStatus?.isExpired && (
                  <button
                    onClick={handleRegenerateOTP}
                    disabled={regenerating}
                    className="w-full py-2.5 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 active:scale-95"
                  >
                    {regenerating ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Regenerate OTP
                      </>
                    )}
                  </button>
                )} */}
              </>
            )}
          </motion.div>
        )}

        {/* ── Staff Info ── */}
        {booking.staffId && (
          <div className="bg-white rounded-2xl border shadow-sm p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <User size={16} /> Assigned Staff
            </h3>
            <div className="flex items-center gap-3">
              {booking.staffId.profilePicture ? (
                <img
                  src={booking.staffId.profilePicture}
                  alt={booking.staffId.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-blue-100"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primaryColor via-brandAccentColor to-primaryColor text-white flex items-center justify-center text-lg font-bold">
                  {booking.staffId.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold text-gray-900">
                  {booking.staffId.name}
                </p>
                <p className="text-sm text-primaryColor">
                  {booking.staffId.designation || "Staff Member"}
                </p>
              </div>
              <button
                onClick={() =>
                  (window.location.href = `tel:${booking.staffId.phoneNumber}`)
                }
                className="p-2 bg-primaryColor/10 rounded-full hover:bg-blue-100 transition"
              >
                <Phone size={18} className="text-primaryColor" />
              </button>
            </div>
          </div>
        )}

        {/* ── PACKAGE: package info + included services ── */}
        {isPackage && (
          <div className="bg-white rounded-2xl border shadow-sm p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Package size={16} /> Package
            </h3>

            {/* Package name + category */}
            <div className="mb-4">
              <p className="font-semibold text-gray-900 text-base">{pkgName}</p>
              {pkgCategory && (
                <p className="text-xs text-gray-400 uppercase tracking-wide mt-0.5">
                  {pkgCategory}
                </p>
              )}
            </div>

            {/* Package pricing */}
            <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-1.5">
              {pkgDiscountedPrice && pkgDiscountedPrice < pkgOriginalPrice ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Original Price</span>
                    <span className="line-through text-gray-400">
                      ₹{pkgOriginalPrice.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600 font-semibold">
                    <span>Package Price</span>
                    <span>₹{pkgDiscountedPrice.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-xs text-green-500">
                    <span>You save</span>
                    <span>
                      ₹
                      {(pkgOriginalPrice - pkgDiscountedPrice).toLocaleString(
                        "en-IN",
                      )}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Package Price</span>
                  <span className="font-semibold text-gray-900">
                    ₹{pkgOriginalPrice.toLocaleString("en-IN")}
                  </span>
                </div>
              )}
            </div>

            {/* Included services */}
            {pkgServices.length > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Included Services ({pkgServices.length})
                </p>
                <div className="space-y-2">
                  {pkgServices.map((svc, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2.5 border border-gray-200 rounded-xl p-2.5 bg-gray-50"
                    >
                      <div className="w-7 h-7 rounded-full bg-accentTextColor/10 flex items-center justify-center flex-shrink-0">
                        <Scissors size={13} className="text-accentTextColor" />
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        {svc}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Payment summary */}
            <div className="pt-3 mt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Final Payable</span>
                <span className="text-lg font-bold text-primaryColor">
                  ₹{finalPayable.toLocaleString("en-IN")}
                </span>
              </div>
              {booking?.payment?.status === "partially_paid" && (
                <>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Paid</span>
                    <span>
                      ₹{booking.payment.paidAmount?.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-red-600 font-semibold">
                    <span>Remaining</span>
                    <span>
                      ₹
                      {booking.payment.remainingAmount?.toLocaleString("en-IN")}
                    </span>
                  </div>
                </>
              )}
              {booking?.payment?.status === "paid" && (
                <div className="flex justify-between text-sm text-green-600 font-semibold">
                  <span>Paid</span>
                  <span>₹{finalPayable.toLocaleString("en-IN")}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── SERVICE: services + billing (unchanged from original) ── */}
        {!isPackage && (
          <div className="bg-white rounded-2xl border shadow-sm p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Scissors size={16} /> Services ({booking.services?.length || 0})
            </h3>
            <div className="space-y-3">
              {booking.services?.map((service, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-3 bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm md:text-base">
                        {service.serviceId?.name || "Service"}
                      </p>
                      {service.brand && (
                        <p className="text-xs text-gray-500">
                          Brand: {service.brand}
                        </p>
                      )}
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      ₹
                      {(
                        (service.finalPrice ?? 0) * (service.qty ?? 1)
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {service.serviceId?.duration || "N/A"}
                    </span>
                    <span>•</span>
                    <span>Qty: {service.qty}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Billing Summary */}
            <div className="pt-3 mt-3 border-t space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Amount</span>
                <span
                  className={`text-lg font-semibold ${hasDiscount ? "line-through text-gray-400" : "text-gray-900"
                    }`}
                >
                  ₹{totalAmount.toLocaleString()}
                </span>
              </div>

              {hasDiscount && (
                <div className="flex justify-between items-center text-green-600 text-xs md:text-sm">
                  <span>
                    Discount
                    {booking?.coupon?.code && ` (${booking.coupon.code})`}
                    {" "}({Math.ceil(discountPct)}% OFF)
                  </span>
                  <span>−₹{totalDiscount.toLocaleString()}</span>
                </div>
              )}

              <div className="pt-2 border-t space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    Final Payable
                  </span>
                  <span className="text-lg font-bold text-primaryColor">
                    ₹{finalPayable.toLocaleString()}
                  </span>
                </div>

                {booking?.payment?.status === "partially_paid" && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Paid</span>
                      <span>
                        ₹{booking.payment.paidAmount?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-red-600 font-semibold">
                      <span>Remaining</span>
                      <span>
                        ₹{booking.payment.remainingAmount?.toLocaleString()}
                      </span>
                    </div>
                  </>
                )}

                {booking?.payment?.status === "paid" && (
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>Paid</span>
                    <span>₹{finalPayable.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Schedule (shared) ── */}
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
              <Calendar size={16} className="text-purple-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-800">Schedule</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Service Date</p>
              <p className="text-sm font-semibold text-gray-900">
                {formatDate(scheduleDate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Time Slot</p>
              <p className="text-sm font-semibold text-gray-900">
                {convertTo12Hour(booking.slotSnapshot?.startTime)} –{" "}
                {convertTo12Hour(booking.slotSnapshot?.endTime)}
              </p>
            </div>
          </div>
        </div>

        {/* ── Location (shared) ── */}
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
              <MapPin size={16} className="text-green-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-800">
              Service Location
            </h3>
          </div>
          <p className="text-sm font-semibold text-gray-900 mb-1">
            {booking.location?.address?.locality},{" "}
            {booking.location?.address?.city}
          </p>
          <p className="text-xs text-gray-600 leading-relaxed">
            {booking.location?.address?.formatted || "N/A"}
          </p>
          {booking.location?.coordinates && (
            <a
              href={`https://www.google.com/maps?q=${booking.location.coordinates[1]},${booking.location.coordinates[0]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-green-50 text-green-700 rounded-lg border border-green-200 font-medium text-sm hover:bg-green-100 transition"
            >
              <MapPin size={16} />
              View on Map
            </a>
          )}
        </div>

        {/* ── Cancel Reason (shared) ── */}
        {booking.status === "cancelled" && booking.cancelReason && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-red-900 mb-1">
              Cancellation Reason
            </p>
            <p className="text-sm text-red-700">{booking.cancelReason}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserBookingDetails;



